import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Content,
  Header,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import {
  applyFilters,
  applyKubernetesFilters,
  applyUnownedFilters,
  bucketTotals,
  envOptions,
  estateTotal,
  filteredBucketTotals,
  isNarrowed,
  groupByService,
  mergeServiceCosts,
  mergedTotals,
  money,
  percent,
  sortMergedRows,
  totalsByResourceType,
  NO_FILTER,
  serviceKey,
  type Filters,
  type KubernetesSummary,
  type SortKey,
  type Summary,
} from './costModel';
import { type ServiceAllocation } from './lib';

type SortState = { key: SortKey; dir: 1 | -1 };

type Remote<T> =
  | { kind: 'loading' }
  | { kind: 'error'; error: Error }
  | { kind: 'loaded'; data: T };

/** Bucket hues are the page's information architecture, not decoration, so they
 * live next to the copy rather than inside makeStyles — a style rule cannot be
 * read back as a value. */
const BUCKET_HUE = {
  light: { cluster: '#B26A12', service: '#0B6E78', shared: '#5F4F98', unallocated: '#B23B31' },
  dark: { cluster: '#E0A050', service: '#3FB6BE', shared: '#9E8FE0', unallocated: '#E2705F' },
} as const;

const BUCKET_FILL = {
  light: { cluster: '#F0DFC6', service: '#CDE6E8', shared: '#DDD7EE', unallocated: '#F2D3CF' },
  dark: { cluster: '#3A2E1C', service: '#123238', shared: '#2A2545', unallocated: '#3B211D' },
} as const;

type BucketKey = keyof typeof BUCKET_HUE.light;

const BUCKET_COPY: Record<string, { label: string; note: string }> = {
  cluster: {
    label: 'Cluster',
    note: 'The AKS clusters themselves: nodes, disks and load balancers. Azure bills it per subscription with no service label, so Azure cannot split it. The Kubernetes column below is OpenCost dividing this same spend, measured separately.',
  },
  service: {
    label: 'Service',
    note: 'Databases, storage and networking belonging to a single service, matched by its resource group or a service tag. This is the spend a team can actually change.',
  },
  shared: {
    label: 'Shared',
    note: 'Platform infrastructure everyone runs on, like API Management and authentication. There is no fair way to divide it between teams, so it is not divided.',
  },
  unallocated: {
    label: 'Unallocated',
    note: 'Resources that should belong to a service but match none. Nearly always a missing tag, or a resource group that skips the naming convention.',
  },
};

/** Figures are monospaced so digits align down a column; prose is not. */
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/** A figure the source could not report, as distinct from one it reported as zero. */
const DASH = '—';

/** Only the non-zero parts, so the caption's terms add up to the row's total. */
const breakdown = (a: ServiceAllocation): string =>
  (
    [
      ['CPU', a.cpuCost],
      ['Memory', a.ramCost],
      ['Storage', a.pvCost],
      ['Network', a.networkCost],
      ['Load balancer', a.loadBalancerCost],
    ] as const
  )
    .filter(([, cost]) => cost > 0)
    .map(([label, cost]) => `${label} ${money(cost)}`)
    .join(' · ');

const useStyles = makeStyles(theme => {
  const mode = theme.palette.type === 'dark' ? 'dark' : 'light';
  const eyebrow = {
    fontFamily: theme.typography.overline.fontFamily,
    fontSize: theme.typography.overline.fontSize,
    letterSpacing: theme.typography.overline.letterSpacing,
    textTransform: 'uppercase' as const,
    fontWeight: 500,
    color: theme.palette.text.hint,
  };
  return {
    stack: { display: 'flex', flexDirection: 'column', gap: theme.spacing(3) },
    panel: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 3,
    },
    hero: { padding: theme.spacing(3), display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
    figure: {
      fontFamily: MONO,
      fontVariantNumeric: 'tabular-nums',
      fontSize: theme.typography.h3.fontSize,
      fontWeight: 600,
      letterSpacing: '-0.03em',
      lineHeight: 1,
    },
    bar: {
      display: 'flex',
      height: 52,
      borderRadius: 2,
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
    },
    seg: {
      display: 'flex',
      alignItems: 'flex-end',
      padding: '7px 9px',
      minWidth: 2,
      fontFamily: MONO,
      fontSize: theme.typography.caption.fontSize,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    },
    legend: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
      gap: theme.spacing(2),
    },
    legendItem: { paddingTop: theme.spacing(1.2), borderTop: '2px solid' },
    // Deliberately outside the proportional bar: showing an unfiltered
    // magnitude beside filtered ones would misstate every proportion.
    excluded: {
      display: 'flex',
      gap: theme.spacing(1.5),
      alignItems: 'baseline',
      padding: theme.spacing(1.5, 2),
      borderRadius: 2,
      border: `1px dashed ${theme.palette.divider}`,
      background: theme.palette.action.hover,
    },
    excludedFig: {
      fontFamily: MONO,
      fontVariantNumeric: 'tabular-nums',
      fontSize: theme.typography.subtitle2.fontSize,
      fontWeight: 600,
      color: BUCKET_HUE[mode].cluster,
      whiteSpace: 'nowrap',
    },
    legendFig: {
      fontFamily: MONO,
      fontVariantNumeric: 'tabular-nums',
      fontSize: theme.typography.subtitle2.fontSize,
      fontWeight: 600,
    },
    filters: { display: 'flex', gap: theme.spacing(1.5), flexWrap: 'wrap', alignItems: 'center' },
    picker: { minWidth: 190 },
    chip: { fontFamily: MONO, borderRadius: 2 },
    label: eyebrow,
    /** The Azure / Kubernetes dividers inside an expanded row: same eyebrow,
     * heavier, so the two blocks separate at a glance. */
    groupLabel: { ...eyebrow, fontWeight: 700, color: theme.palette.text.secondary },
    head: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      padding: theme.spacing(1.6, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tableWrap: { overflowX: 'auto' },
    cellHead: {
      ...eyebrow,
      padding: theme.spacing(1.1, 2),
      whiteSpace: 'nowrap',
    },
    cell: { padding: theme.spacing(1, 2) },
    mono: {
      fontFamily: MONO,
      fontVariantNumeric: 'tabular-nums',
      fontSize: theme.typography.body2.fontSize,
    },
    row: { cursor: 'pointer', '&:hover': { background: theme.palette.action.hover } },
    envTag: {
      fontFamily: MONO,
      fontSize: theme.typography.caption.fontSize,
      height: 20,
      borderRadius: 2,
      marginRight: 4,
    },
    split: {
      display: 'flex',
      height: 4,
      borderRadius: 1,
      overflow: 'hidden',
      minWidth: 1,
    },
    finding: {
      padding: theme.spacing(1.8, 2),
      borderTop: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.8),
    },
    empty: {
      padding: theme.spacing(4),
      textAlign: 'center',
      color: theme.palette.text.hint,
      fontSize: theme.typography.body2.fontSize,
    },
  };
});

export const CostConsolePage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const mode = theme.palette.type === 'dark' ? 'dark' : 'light';
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [azure, setAzure] = useState<Remote<Summary>>({ kind: 'loading' });
  const [kubernetes, setKubernetes] = useState<Remote<KubernetesSummary>>({
    kind: 'loading',
  });
  const [filters, setFilters] = useState<Filters>({
    env: NO_FILTER,
    resourceType: NO_FILTER,
    service: NO_FILTER,
  });
  const [sort, setSort] = useState<SortState>({ key: 'cost', dir: -1 });
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = async <T,>(
      pluginId: string,
      set: (r: Remote<T>) => void,
    ): Promise<void> => {
      try {
        const base = await discovery.getBaseUrl(pluginId);
        const res = await fetchApi.fetch(`${base}/summary`);
        if (res.status === 403) {
          throw new Error('Cost data is restricted to platform administrators.');
        }
        if (!res.ok) {
          throw new Error(`Could not load cost data (${res.status}).`);
        }
        const body = (await res.json()) as T;
        if (live) set({ kind: 'loaded', data: body });
      } catch (e) {
        if (live) set({ kind: 'error', error: e as Error });
      }
    };
    // Two independent states on purpose: Kubernetes being unreachable must not
    // blank the Azure figures, which is the same invariant the entity card keeps.
    load<Summary>('cost', setAzure);
    load<KubernetesSummary>('cost-allocation', setKubernetes);
    return () => {
      live = false;
    };
  }, [discovery, fetchApi]);

  const toggle = useCallback((service: string) => {
    setOpen(current => (current === service ? null : service));
  }, []);

  const data = azure.kind === 'loaded' ? azure.data : null;
  const k8sData = kubernetes.kind === 'loaded' ? kubernetes.data : null;
  // The Resource filter is an Azure resource type with no Kubernetes analogue,
  // so while it is set the table answers a purely Azure question.
  const azureOnly = filters.resourceType !== NO_FILTER;

  const view = useMemo(() => {
    if (!data) return null;
    const narrowed = isNarrowed(filters);
    const filtered = applyFilters(data.services, filters);
    const unowned = applyUnownedFilters(data.unowned, filters);
    const cluster = bucketTotals(data.buckets).find(b => b.bucket === 'cluster')?.cost ?? 0;
    const buckets = filteredBucketTotals(filtered, unowned, cluster, narrowed);
    const total = buckets.reduce((a, b) => a + b.cost, 0);
    const k8sRows =
      k8sData && !azureOnly
        ? applyKubernetesFilters(k8sData.services, filters)
        : [];
    // Sorting by cost is Azure-only until the Kubernetes fetch lands, then
    // re-sorts. That is correct, not a race to "fix".
    const rows = sortMergedRows(
      mergeServiceCosts(groupByService(filtered), k8sRows),
      sort.key,
      sort.dir,
    );
    return {
      narrowed,
      buckets,
      total,
      share: total > 0 ? (buckets.find(b => b.bucket === 'service')?.cost ?? 0) / total : 0,
      rows,
      totals: mergedTotals(rows),
      max: Math.max(...rows.map(r => r.cost), 1),
      types: totalsByResourceType(filtered),
      cluster,
      estate: estateTotal(data.buckets),
      envs: envOptions(data.services, data.unowned),
      resourceTypes: [...new Set(data.services.map(s => s.resourceType))].sort(),
      // Kubernetes-only services are the common case, so the picker has to know
      // about both sources or most of the table is unreachable from it. Deduped
      // by the same folded key the filter and the join use, Azure spelling
      // first, so one service can never appear as two options.
      services: [
        ...[
          ...data.services.map(s => s.service),
          ...(k8sData?.services ?? []).map(s => s.service),
        ]
          .reduce((byKey, name) => {
            // First wins, and Azure is listed first, so the option matches the
            // spelling mergeServiceCosts puts in the table.
            if (!byKey.has(serviceKey(name))) byKey.set(serviceKey(name), name);
            return byKey;
          }, new Map<string, string>())
          .values(),
      ].sort(),
    };
  }, [data, k8sData, filters, sort, azureOnly]);

  if (azure.kind === 'error') return <ResponseErrorPanel error={azure.error} />;
  if (!view || !data) return <Progress />;

  const grand = view.total;
  const reachable = k8sData?.clusters.filter(c => c.status === 'ok') ?? [];
  const showKubernetes = !azureOnly;
  // Columns can be shown before any figure exists. Totals and the bar must wait
  // for real data, so an unknown Kubernetes cost never renders as zero.
  // A loaded response with no cluster reporting is still unknown, not zero: the
  // cache is cold after a restart, or every cluster is unreachable. Gating only
  // on 'loaded' made the header claim "Kubernetes $0.00" and the Total column
  // present the Azure figure as complete, while the footer on the same screen
  // said no cluster had reported in.
  const haveKubernetes =
    showKubernetes && kubernetes.kind === 'loaded' && reachable.length > 0;
  const columnCount = showKubernetes ? 6 : 4;

  let servicesCaption =
    'Azure figures only for now — the Kubernetes column fills in once OpenCost reports.';
  if (!showKubernetes) {
    servicesCaption =
      'Kubernetes has no resource types, so it is set aside while a resource filter is active. These are Azure figures only.';
  } else if (haveKubernetes) {
    servicesCaption =
      'What each service costs to run: its own Azure resources, plus OpenCost dividing up the cluster it runs in. The Kubernetes column is the Cluster bucket above split apart, not spend on top of it, and the two windows differ by a day or two.';
  }

  const picker = (
    id: string,
    label: string,
    value: string,
    options: string[],
    onPick: (v: string) => void,
    render: (v: string) => string = v => v,
  ) => (
    <FormControl variant="outlined" size="small" className={classes.picker}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        value={value}
        label={label}
        onChange={e => onPick(e.target.value as string)}
      >
        <MenuItem value={NO_FILTER}>All {label.toLowerCase()}</MenuItem>
        {options.map(o => (
          <MenuItem key={o} value={o}>
            {render(o)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Page themeId="tool">
      <Header
        title="Azure cost"
        subtitle={
          data.window.start
            ? `${data.window.start} to ${data.window.end} · 7 complete days · USD amortized`
            : 'Waiting for the first collection'
        }
      />
      <Content>
        <div className={classes.stack}>
          <div className={classes.filters}>
            {picker('filter-env', 'Environment', filters.env, view.envs, v =>
              setFilters(f => ({ ...f, env: v })),
            )}
            {picker('filter-service', 'Service', filters.service, view.services, v =>
              setFilters(f => ({ ...f, service: v })),
            )}
            {picker(
              'filter-resource',
              'Resource',
              filters.resourceType,
              view.resourceTypes,
              v => setFilters(f => ({ ...f, resourceType: v })),
              t => t.replace(/^Azure (Database for )?/, ''),
            )}
            {view.narrowed && (
              <Chip
                size="small"
                variant="outlined"
                className={classes.chip}
                label="Clear filters"
                onClick={() =>
                  setFilters({ env: NO_FILTER, resourceType: NO_FILTER, service: NO_FILTER })
                }
              />
            )}
          </div>

          <div className={`${classes.panel} ${classes.hero}`}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
              <span className={classes.figure}>{money(grand)}</span>
              <Typography variant="body2" color="textSecondary">
                {view.narrowed
                  ? `of ${money(view.estate)} billed across the estate · ${percent(view.share)} of what is shown belongs to a service`
                  : `across three subscriptions · ${percent(view.share)} resolves to a service in Azure's own tags`}
              </Typography>
            </div>

            {view.narrowed && (
              <div className={classes.excluded}>
                <div className={classes.excludedFig}>{money(view.cluster)}</div>
                <Typography variant="caption" color="textSecondary">
                  <strong>Cluster, not in this view.</strong> It is billed per subscription with
                  no environment, service or resource type attached, so it cannot be narrowed the
                  way the rest of this page can. It is the bulk of the difference between the
                  figure above and the estate total.
                </Typography>
              </div>
            )}

            <div
              className={classes.bar}
              role="img"
              aria-label={view.buckets
                .map(b => `${BUCKET_COPY[b.bucket].label} ${percent(grand ? b.cost / grand : 0)}`)
                .join(', ')}
            >
              {view.buckets.map(b => (
                <div
                  key={b.bucket}
                  className={classes.seg}
                  style={{
                    flex: Math.max(b.cost, 0.0001),
                    background: BUCKET_FILL[mode][b.bucket as BucketKey],
                    borderRight: `2px solid ${BUCKET_HUE[mode][b.bucket as BucketKey]}`,
                    color: BUCKET_HUE[mode][b.bucket as BucketKey],
                  }}
                >
                  <span>
                    {grand && b.cost / grand > 0.03
                      ? `${BUCKET_COPY[b.bucket].label} ${percent(b.cost / grand)}`
                      : ''}
                  </span>
                </div>
              ))}
            </div>

            <div className={classes.legend}>
              {view.buckets.map(b => (
                <div
                  key={b.bucket}
                  className={classes.legendItem}
                  style={{ borderTopColor: BUCKET_HUE[mode][b.bucket as BucketKey] }}
                >
                  <div className={classes.legendFig}>{money(b.cost)}</div>
                  <Typography variant="caption" color="textSecondary">
                    <strong>{BUCKET_COPY[b.bucket].label}</strong> — {BUCKET_COPY[b.bucket].note}
                  </Typography>
                </div>
              ))}
            </div>
          </div>

          <div className={classes.panel}>
            <div className={classes.head}>
              <Typography variant="subtitle2">Services</Typography>
              <span className={classes.label}>
                {view.rows.length} service{view.rows.length === 1 ? '' : 's'} ·{' '}
                {haveKubernetes
                  ? `Azure ${money(view.totals.azure)} + Kubernetes ${money(view.totals.kubernetes)} = ${money(view.totals.total)}`
                  : `Azure ${money(view.totals.azure)}`}
              </span>
            </div>
            <div style={{ padding: '10px 16px 0' }}>
              <Typography variant="caption" color="textSecondary">
                {servicesCaption}
              </Typography>
            </div>
            <div className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.cellHead}>
                      <TableSortLabel
                        active={sort.key === 'name'}
                        direction={sort.dir === -1 ? 'desc' : 'asc'}
                        onClick={() =>
                          setSort(s => ({ key: 'name', dir: s.key === 'name' ? ((s.dir * -1) as 1 | -1) : 1 }))
                        }
                      >
                        Service
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.cellHead}>Environments</TableCell>
                    <TableCell className={classes.cellHead} align="right">
                      <TableSortLabel
                        active={sort.key === 'azure'}
                        direction={sort.dir === -1 ? 'desc' : 'asc'}
                        onClick={() =>
                          setSort(s => ({ key: 'azure', dir: s.key === 'azure' ? ((s.dir * -1) as 1 | -1) : -1 }))
                        }
                      >
                        {showKubernetes ? 'Azure' : 'Cost'}
                      </TableSortLabel>
                    </TableCell>
                    {showKubernetes && (
                      <TableCell className={classes.cellHead} align="right">
                        <TableSortLabel
                          active={sort.key === 'kubernetes'}
                          direction={sort.dir === -1 ? 'desc' : 'asc'}
                          onClick={() =>
                            setSort(s => ({ key: 'kubernetes', dir: s.key === 'kubernetes' ? ((s.dir * -1) as 1 | -1) : -1 }))
                          }
                        >
                          Kubernetes
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {showKubernetes && (
                      <TableCell className={classes.cellHead} align="right">
                        <TableSortLabel
                          active={sort.key === 'cost'}
                          direction={sort.dir === -1 ? 'desc' : 'asc'}
                          onClick={() =>
                            setSort(s => ({ key: 'cost', dir: s.key === 'cost' ? ((s.dir * -1) as 1 | -1) : -1 }))
                          }
                        >
                          Total
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell className={classes.cellHead} style={{ width: 160 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {view.rows.length === 0 && (
                    <TableRow>
                      <TableCell className={classes.empty} colSpan={columnCount}>
                        No services match this filter. Clear a filter to see spend.
                      </TableCell>
                    </TableRow>
                  )}
                  {view.rows.map(r => (
                    <Fragment key={r.service}>
                      <TableRow
                        hover
                        className={classes.row}
                        tabIndex={0}
                        aria-expanded={open === r.service}
                        onClick={() => toggle(r.service)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggle(r.service);
                          }
                        }}
                      >
                        <TableCell className={`${classes.cell} ${classes.mono}`}>{r.service}</TableCell>
                        <TableCell className={classes.cell}>
                          {r.envs.map(e => (
                            <Chip key={e} size="small" variant="outlined" label={e} className={classes.envTag} />
                          ))}
                        </TableCell>
                        <TableCell className={`${classes.cell} ${classes.mono}`} align="right">
                          {r.azureLines.length > 0 ? money(r.azure) : DASH}
                        </TableCell>
                        {showKubernetes && (
                          <TableCell className={`${classes.cell} ${classes.mono}`} align="right">
                            {haveKubernetes && r.kubernetesLines.length > 0
                              ? money(r.kubernetes)
                              : DASH}
                          </TableCell>
                        )}
                        {showKubernetes && (
                          <TableCell
                            className={`${classes.cell} ${classes.mono}`}
                            align="right"
                            style={{ fontWeight: 600 }}
                          >
                            {haveKubernetes ? money(r.cost) : DASH}
                          </TableCell>
                        )}
                        <TableCell className={classes.cell}>
                          <div
                            className={classes.split}
                            role="img"
                            aria-label={
                              haveKubernetes
                                ? `Azure ${money(r.azure)}, Kubernetes ${money(r.kubernetes)}`
                                : `Azure ${money(r.azure)}`
                            }
                            style={{ width: `${Math.max(2, (r.cost / view.max) * 100)}%` }}
                          >
                            <div style={{ flex: Math.max(r.azure, 0.0001), background: BUCKET_HUE[mode].service }} />
                            {haveKubernetes && r.kubernetes > 0 && (
                              <div style={{ flex: r.kubernetes, background: BUCKET_HUE[mode].cluster }} />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>

                      {open === r.service && r.azureLines.length > 0 && r.kubernetesLines.length > 0 && (
                        <TableRow>
                          <TableCell className={classes.cell} colSpan={columnCount} style={{ paddingLeft: 34 }}>
                            <span className={classes.groupLabel}>Azure resources</span>
                          </TableCell>
                        </TableRow>
                      )}
                      {open === r.service &&
                        r.azureLines.map(l => (
                          <TableRow key={`${r.service}-azure-${l.env}-${l.resourceType}`}>
                            <TableCell className={`${classes.cell} ${classes.mono}`} colSpan={2} style={{ paddingLeft: 34, opacity: 0.75 }}>
                              {l.resourceType} · {l.env}
                            </TableCell>
                            <TableCell className={`${classes.cell} ${classes.mono}`} align="right" style={{ opacity: 0.75 }}>
                              {money(l.cost)}
                            </TableCell>
                            {showKubernetes && <TableCell className={classes.cell} colSpan={2} />}
                            <TableCell className={classes.cell} />
                          </TableRow>
                        ))}

                      {open === r.service && r.kubernetesLines.length > 0 && (
                        <>
                          {r.azureLines.length > 0 && (
                            <TableRow>
                              <TableCell className={classes.cell} colSpan={columnCount} style={{ paddingLeft: 34 }}>
                                <span className={classes.groupLabel}>Kubernetes</span>
                              </TableCell>
                            </TableRow>
                          )}
                          {r.kubernetesLines.map(l => (
                            <TableRow key={`${r.service}-k8s-${l.environment}`}>
                              <TableCell className={`${classes.cell} ${classes.mono}`} colSpan={2} style={{ paddingLeft: 34, opacity: 0.75 }}>
                                {l.environment}
                                {l.allocation && (
                                  <Typography variant="caption" color="textSecondary" component="div">
                                    {breakdown(l.allocation)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell className={classes.cell} />
                              <TableCell className={`${classes.cell} ${classes.mono}`} align="right" style={{ opacity: 0.75 }}>
                                {money(l.cost)}
                              </TableCell>
                              <TableCell className={classes.cell} />
                              <TableCell className={classes.cell} />
                            </TableRow>
                          ))}
                        </>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>

            {showKubernetes && kubernetes.kind === 'loading' && (
              <div className={classes.finding}>
                <Typography variant="caption" color="textSecondary">
                  Kubernetes cost still loading — Azure figures are complete.
                </Typography>
              </div>
            )}
            {showKubernetes && kubernetes.kind === 'error' && (
              <div className={classes.finding}>
                <Typography variant="caption" color="textSecondary">
                  Kubernetes cost unavailable — {kubernetes.error.message} Azure figures are unaffected.
                </Typography>
              </div>
            )}
            {showKubernetes && kubernetes.kind === 'loaded' && reachable.length === 0 && (
              <div className={classes.finding}>
                <Typography variant="caption" color="textSecondary">
                  Kubernetes cost is currently unavailable — no cluster reported in.
                </Typography>
              </div>
            )}
            {showKubernetes &&
              (k8sData?.clusters ?? [])
                .filter(c => c.status !== 'ok')
                .map(c => (
                  <div className={classes.finding} key={c.name}>
                    <Typography variant="caption" color="textSecondary">
                      {c.asOf
                        ? `Some figures may be stale — ${c.name} last updated ${c.asOf}.`
                        : `No data yet from ${c.name}.`}
                    </Typography>
                  </div>
                ))}
          </div>

          <div className={classes.panel}>
            <div className={classes.head}>
              <Typography variant="subtitle2">Where Azure service money goes</Typography>
            </div>
            <div style={{ padding: 16 }} className={classes.legend}>
              {view.types.length === 0 && <div className={classes.empty}>Nothing in this filter.</div>}
              {view.types.map(t => (
                <div key={t.type}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <span className={classes.mono} style={{ fontSize: 11.5 }}>
                      {t.type}
                    </span>
                    <span className={classes.mono}>{money(t.cost)}</span>
                  </div>
                  <div
                    style={{
                      height: 3,
                      marginTop: 4,
                      background: BUCKET_HUE[mode].service,
                      width: `${(t.cost / Math.max(...view.types.map(x => x.cost), 1)) * 100}%`,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Content>
    </Page>
  );
};
